// CVCreditsAdmin.jsx — Interface admin pour gérer les crédits CV
// Fonctionnalités :
//   • Liste de tous les utilisateurs avec leur solde de crédits CV
//   • Recherche par nom / email
//   • Ajouter des crédits à un utilisateur (pack 10 par 10 ou montant libre)
//   • Historique des transactions par user
//   • Stats globales (total crédits vendus, total utilisés, revenus)

import React, { useState, useEffect } from 'react';
import {
  FileText, Search, Plus, History, RefreshCw,
  ChevronDown, ChevronUp, CheckCircle, XCircle,
  TrendingUp, Users, CreditCard, Zap, X
} from 'lucide-react';
import { API_URL } from '../../config';

// ── Helpers ──────────────────────────────────────────────────────────────────
// Auth via JWT Appwrite (rôle admin) — pas besoin de clé séparée
const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json',
});

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
export default function CVCreditsAdmin() {
  const [users, setUsers]           = useState([]);
  const [filtered, setFiltered]     = useState([]);
  const [search, setSearch]         = useState('');
  const [loading, setLoading]       = useState(true);
  const [stats, setStats]           = useState(null);

  // Modal ajout crédits
  const [modalUser, setModalUser]   = useState(null);
  const [addAmount, setAddAmount]   = useState(10);
  const [addNote, setAddNote]       = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addSuccess, setAddSuccess] = useState(false);

  // Modal historique
  const [histUser, setHistUser]     = useState(null);
  const [history, setHistory]       = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      users.filter(u =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
      )
    );
  }, [search, users]);

  // ── Charger tous les users + leurs soldes ─────────────────────────────────
  const loadAll = async () => {
    setLoading(true);
    try {
      // 1. Liste des users
      const usersRes = await fetch(`${API_URL}/api/admin/users`, {
        headers: authHeaders(),
      });
      const usersData = usersRes.ok ? await usersRes.json() : { users: [] };
      const userList  = usersData.users || [];

      // 2. Tous les soldes CV
      const creditsRes = await fetch(`${API_URL}/cv/admin/credits/all`, {
        headers: authHeaders(),
      });
      const creditsData = creditsRes.ok ? await creditsRes.json() : { credits: [] };
      const creditMap   = {};
      (creditsData.credits || []).forEach(c => {
        creditMap[c.user_id] = c;
      });

      // 3. Fusionner — tenter $id puis id puis user_id selon ce que retourne l'API
      const merged = userList.map(u => {
        const uid = u.$id || u.id || u.user_id || '';
        const credit = creditMap[uid] || {};
        return {
          ...u,
          _uid:            uid,
          cv_balance:      credit.balance      ?? 0,
          cv_total_bought: credit.total_bought ?? 0,
          cv_total_used:   credit.total_used   ?? 0,
        };
      });

      setUsers(merged);
      setFiltered(merged);

      // 4. Stats globales
      const totalBalance = merged.reduce((s, u) => s + u.cv_balance,      0);
      const totalBought  = merged.reduce((s, u) => s + u.cv_total_bought, 0);
      const totalUsed    = merged.reduce((s, u) => s + u.cv_total_used,   0);
      setStats({
        totalBalance,
        totalBought,
        totalUsed,
        revenue: totalBought * 1500, // 1 crédit = 1 500 Ar
        usersWithCredits: merged.filter(u => u.cv_balance > 0).length,
      });

    } catch (e) {
      console.error('loadAll error:', e);
    } finally {
      setLoading(false);
    }
  };

  // ── Ajouter des crédits ───────────────────────────────────────────────────
  const handleAdd = async () => {
    if (!modalUser || addAmount <= 0) return;
    setAddLoading(true);
    setAddSuccess(false);
    try {
      const res = await fetch(`${API_URL}/cv/credits/add`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          user_id: modalUser._uid,
          amount:  addAmount,
          note:    addNote || `Ajout admin — ${addAmount} crédits`,
          type:    'admin_add',
        }),
      });
      const data = await res.json();
      if (data.success) {
        setAddSuccess(true);
        // Mettre à jour le user dans la liste
        const updater = u => u._uid === modalUser._uid
          ? { ...u, cv_balance: data.new_balance }
          : u;
        setUsers(prev => prev.map(updater));
        setFiltered(prev => prev.map(updater));
        setModalUser(prev => ({ ...prev, cv_balance: data.new_balance }));
        setAddNote('');
        setTimeout(() => setAddSuccess(false), 3000);
      } else {
        alert('Erreur : ' + (data.error || 'inconnue'));
      }
    } catch (e) {
      alert('Erreur réseau');
    } finally {
      setAddLoading(false);
    }
  };

  // ── Charger historique d'un user ─────────────────────────────────────────
  const loadHistory = async (user) => {
    setHistUser(user);
    setHistLoading(true);
    setHistory([]);
    try {
      const res = await fetch(
        `${API_URL}/cv/admin/credits/history/${user._uid}`,
        { headers: authHeaders() }
      );
      const data = res.ok ? await res.json() : { transactions: [] };
      setHistory(data.transactions || []);
    } catch (e) {
      console.error(e);
    } finally {
      setHistLoading(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileText className="w-7 h-7 text-violet-400" />
            Crédits CV IA
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Gérez les crédits de génération de CV par utilisateur
          </p>
        </div>
        <button
          onClick={loadAll}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all text-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* ── Stats globales ── */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <StatCard icon={CreditCard}  label="Crédits vendus"   value={stats.totalBought}       color="from-violet-600 to-purple-700" />
          <StatCard icon={Zap}         label="Crédits utilisés" value={stats.totalUsed}          color="from-blue-600 to-cyan-600" />
          <StatCard icon={FileText}    label="Soldes restants"  value={stats.totalBalance}       color="from-emerald-600 to-green-600" />
          <StatCard icon={Users}       label="Users avec crédits" value={stats.usersWithCredits} color="from-pink-600 to-rose-600" />
          <StatCard icon={TrendingUp}  label="Revenus estimés"  value={`${stats.revenue.toLocaleString()} Ar`} color="from-amber-600 to-orange-600" isText />
        </div>
      )}

      {/* ── Barre de recherche ── */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher par nom ou email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 text-sm"
        />
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 text-violet-400 animate-spin" />
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 bg-white/5">
                  <th className="text-left px-4 py-3 text-gray-400 text-xs font-semibold uppercase tracking-wider">Utilisateur</th>
                  <th className="text-center px-4 py-3 text-gray-400 text-xs font-semibold uppercase tracking-wider">Solde</th>
                  <th className="text-center px-4 py-3 text-gray-400 text-xs font-semibold uppercase tracking-wider">Achetés</th>
                  <th className="text-center px-4 py-3 text-gray-400 text-xs font-semibold uppercase tracking-wider">Utilisés</th>
                  <th className="text-center px-4 py-3 text-gray-400 text-xs font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-12 text-gray-500">Aucun utilisateur trouvé</td>
                  </tr>
                ) : filtered.map(user => (
                  <tr key={user._uid} className="hover:bg-white/5 transition-colors">

                    {/* Utilisateur */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-600 to-blue-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                          {(user.name || user.email || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <div className="text-white font-medium text-sm">{user.name || '—'}</div>
                          <div className="text-gray-500 text-xs">{user.email}</div>
                        </div>
                      </div>
                    </td>

                    {/* Solde */}
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold ${
                        user.cv_balance > 0
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}>
                        {user.cv_balance > 0
                          ? <CheckCircle className="w-3 h-3" />
                          : <XCircle className="w-3 h-3" />
                        }
                        {user.cv_balance}
                      </span>
                    </td>

                    {/* Achetés */}
                    <td className="px-4 py-3 text-center">
                      <span className="text-violet-300 font-semibold text-sm">{user.cv_total_bought}</span>
                    </td>

                    {/* Utilisés */}
                    <td className="px-4 py-3 text-center">
                      <span className="text-blue-300 font-semibold text-sm">{user.cv_total_used}</span>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => { setModalUser({ ...user, _uid: user._uid || user.$id || user.id }); setAddAmount(10); setAddNote(''); setAddSuccess(false); }}
                          className="flex items-center gap-1 px-3 py-1.5 bg-violet-600/80 hover:bg-violet-600 text-white rounded-lg text-xs font-medium transition-all"
                        >
                          <Plus className="w-3 h-3" />
                          Crédits
                        </button>
                        <button
                          onClick={() => loadHistory(user)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600/50 hover:bg-blue-600/80 text-blue-200 rounded-lg text-xs font-medium transition-all"
                        >
                          <History className="w-3 h-3" />
                          Historique
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          MODAL — Ajouter des crédits
      ════════════════════════════════════════════════════════════════════ */}
      {modalUser && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-md shadow-2xl">

            {/* Header modal */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-600 to-blue-600 flex items-center justify-center text-white font-bold">
                  {(modalUser.name || modalUser.email || '?')[0].toUpperCase()}
                </div>
                <div>
                  <div className="text-white font-bold">{modalUser.name || modalUser.email}</div>
                  <div className="text-gray-400 text-xs">{modalUser.email}</div>
                </div>
              </div>
              <button onClick={() => setModalUser(null)} className="p-2 hover:bg-white/10 rounded-lg text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Solde actuel */}
            <div className="bg-white/5 rounded-xl p-4 mb-5 flex items-center justify-between">
              <span className="text-gray-400 text-sm">Solde actuel</span>
              <span className={`text-2xl font-bold ${modalUser.cv_balance > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {modalUser.cv_balance} crédit{modalUser.cv_balance !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Montant rapide */}
            <div className="mb-4">
              <label className="text-gray-300 text-sm font-medium mb-2 block">Montant à ajouter</label>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {[5, 10, 20, 50].map(n => (
                  <button
                    key={n}
                    onClick={() => setAddAmount(n)}
                    className={`py-2 rounded-xl text-sm font-bold transition-all ${
                      addAmount === n
                        ? 'bg-violet-600 text-white'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10'
                    }`}
                  >
                    +{n}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  value={addAmount}
                  onChange={e => setAddAmount(Math.max(1, parseInt(e.target.value) || 1))}
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-violet-500"
                />
                <span className="text-gray-400 text-sm">crédits</span>
              </div>
              <div className="text-gray-500 text-xs mt-1">
                ≈ {(addAmount * 1500).toLocaleString()} Ar · Nouveau solde : {modalUser.cv_balance + addAmount} crédits
              </div>
            </div>

            {/* Note */}
            <div className="mb-5">
              <label className="text-gray-300 text-sm font-medium mb-2 block">Note (optionnel)</label>
              <input
                type="text"
                placeholder="ex: Paiement MVola MG2025... validé"
                value={addNote}
                onChange={e => setAddNote(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500"
              />
            </div>

            {/* Succès */}
            {addSuccess && (
              <div className="flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 rounded-xl px-4 py-3 mb-4">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <span className="text-emerald-300 text-sm font-medium">
                  ✅ {addAmount} crédits ajoutés ! Nouveau solde : {modalUser.cv_balance} crédits
                </span>
              </div>
            )}

            {/* Bouton confirmer */}
            <button
              onClick={handleAdd}
              disabled={addLoading || addAmount <= 0}
              className="w-full py-3 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 disabled:opacity-50 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2"
            >
              {addLoading ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Ajouter {addAmount} crédit{addAmount > 1 ? 's' : ''} CV
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          MODAL — Historique transactions
      ════════════════════════════════════════════════════════════════════ */}
      {histUser && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-lg shadow-2xl max-h-[80vh] flex flex-col">

            {/* Header */}
            <div className="flex items-center justify-between mb-5 flex-shrink-0">
              <div>
                <h3 className="text-white font-bold text-lg">Historique crédits CV</h3>
                <p className="text-gray-400 text-sm">
                  {histUser.name || histUser.email}
                </p>
              </div>
              <button onClick={() => setHistUser(null)} className="p-2 hover:bg-white/10 rounded-lg text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Liste */}
            <div className="overflow-y-auto flex-1 space-y-2 pr-1">
              {histLoading ? (
                <div className="flex items-center justify-center py-10">
                  <RefreshCw className="w-6 h-6 text-violet-400 animate-spin" />
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-10 text-gray-500">Aucune transaction</div>
              ) : history.map((tx, i) => (
                <div key={i} className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3 border border-white/5">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      tx.amount > 0
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {tx.amount > 0 ? <Plus className="w-4 h-4" /> : <Zap className="w-4 h-4" />}
                    </div>
                    <div>
                      <div className="text-white text-sm font-medium">
                        {tx.type === 'purchase'  && '💳 Achat'}
                        {tx.type === 'admin_add' && '🔧 Ajout admin'}
                        {tx.type === 'consume'   && '⚡ CV généré'}
                        {!['purchase','admin_add','consume'].includes(tx.type) && tx.type}
                      </div>
                      <div className="text-gray-500 text-xs">
                        {tx.note && <span className="mr-2">{tx.note}</span>}
                        {fmtDate(tx.created_at)}
                      </div>
                      {tx.ref_payment && (
                        <div className="text-gray-600 text-xs font-mono">réf: {tx.ref_payment}</div>
                      )}
                    </div>
                  </div>
                  <span className={`text-lg font-bold ${tx.amount > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ── StatCard ──────────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, color, isText = false }) => (
  <div className={`bg-gradient-to-br ${color} rounded-xl p-4 text-white`}>
    <Icon className="w-6 h-6 opacity-80 mb-2" />
    <div className={`font-bold mb-1 ${isText ? 'text-lg' : 'text-3xl'}`}>{value}</div>
    <div className="text-white/80 text-xs">{label}</div>
  </div>
);